import { sanitizeRevitParameterNames } from '../../revitParameterNames';
import type {
  AdditionalCategoriesConfiguration,
  BoltConfiguration,
  ConnectorBindingConfiguration,
  MenuConfiguration,
  ProcessPropertiesConfiguration,
} from '../../../types';
import { collectAdditionalCategoryTargets } from './additionalCategoryPlan';
import { collectConnectorBindingParameters } from './connectorPlan';
import { pushFormFieldValue } from './formFieldValues';

export function resolveSharedParameterDefinitions(
  parameterNames: string[],
  parsedParameters: Array<{
    name: string;
    guid: string;
    dataType: string;
    groupId: number;
    description?: string;
  }>,
): {
  definitions: Array<{
    name: string;
    guid: string;
    dataType: string;
    groupId: number;
    description?: string;
  }>;
  missingNames: string[];
} {
  const lookup = new Map(
    parsedParameters.map((
      parameter,
    ) => [parameter.name.trim().toLowerCase(), parameter]),
  );
  const definitions: Array<{
    name: string;
    guid: string;
    dataType: string;
    groupId: number;
    description?: string;
  }> = [];
  const missingNames: string[] = [];

  sanitizeRevitParameterNames(parameterNames).forEach((parameterName) => {
    const matchingDefinition = lookup.get(parameterName.trim().toLowerCase());
    if (!matchingDefinition) {
      missingNames.push(parameterName);
      return;
    }

    definitions.push(matchingDefinition);
  });

  return { definitions, missingNames };
}

export function collectProcessConditionParameters(
  processConfig: ProcessPropertiesConfiguration,
): string[] {
  if (!processConfig?.processConditionsChecked) {
    return [];
  }

  const parameterNames: string[] = [];
  pushFormFieldValue(parameterNames, processConfig.processConditionsValue);
  pushFormFieldValue(parameterNames, processConfig.pipingSpecValuePC);
  pushFormFieldValue(parameterNames, processConfig.tracingSpecValuePC);
  pushFormFieldValue(parameterNames, processConfig.attribute12Value);
  pushFormFieldValue(parameterNames, processConfig.attribute13Value);
  pushFormFieldValue(parameterNames, processConfig.attribute14Value);
  pushFormFieldValue(parameterNames, processConfig.attribute15Value);
  pushFormFieldValue(parameterNames, processConfig.attribute16Value);
  pushFormFieldValue(parameterNames, processConfig.attribute17Value);
  pushFormFieldValue(parameterNames, processConfig.attribute18Value);
  pushFormFieldValue(parameterNames, processConfig.attribute19Value);
  pushFormFieldValue(parameterNames, processConfig.attribute20Value);
  pushFormFieldValue(parameterNames, processConfig.attribute21Value);
  pushFormFieldValue(parameterNames, processConfig.attribute22Value);
  pushFormFieldValue(parameterNames, processConfig.attribute23Value);
  return sanitizeRevitParameterNames(parameterNames);
}

export function collectProjectPropertyParameters(
  processConfig: ProcessPropertiesConfiguration,
): string[] {
  if (!processConfig?.projectPropertiesChecked) {
    return [];
  }

  const parameterNames: string[] = [];
  pushFormFieldValue(parameterNames, processConfig.projectIdentifierValue);
  pushFormFieldValue(parameterNames, processConfig.projectPropertiesValue);
  pushFormFieldValue(parameterNames, processConfig.projectNameValue);
  pushFormFieldValue(parameterNames, processConfig.areaValue);
  pushFormFieldValue(parameterNames, processConfig.attribute1ClientName);
  pushFormFieldValue(parameterNames, processConfig.attribute2ProjectAddress);
  pushFormFieldValue(parameterNames, processConfig.attribute3ClientDepartment);
  pushFormFieldValue(
    parameterNames,
    processConfig.attribute4ClientProjectNumber,
  );
  pushFormFieldValue(
    parameterNames,
    processConfig.attribute5ClientDocumentNumber,
  );
  pushFormFieldValue(parameterNames, processConfig.attribute6ClientCode);
  pushFormFieldValue(parameterNames, processConfig.attribute7EngPartyOffice);
  pushFormFieldValue(
    parameterNames,
    processConfig.attribute8EngPartyOfficeLocation,
  );
  pushFormFieldValue(
    parameterNames,
    processConfig.attribute9EngPartyProjectNumber,
  );
  pushFormFieldValue(parameterNames, processConfig.attribute10EngPartyAreaCode);
  return sanitizeRevitParameterNames(parameterNames);
}

export function collectConfiguredParameters(config: {
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
  processConfig: ProcessPropertiesConfiguration;
  boltConfig: BoltConfiguration;
  additionalCategoriesConfig: AdditionalCategoriesConfiguration;
}): string[] {
  const {
    menuConfig,
    connectorConfig,
    processConfig,
    boltConfig,
    additionalCategoriesConfig,
  } = config;

  const parameterNames: string[] = collectConnectorBindingParameters({
    menuConfig,
    connectorConfig,
  });

  pushFormFieldValue(parameterNames, boltConfig?.boltSetActiveValue);
  pushFormFieldValue(parameterNames, boltConfig?.boltItemDescriptionValue);
  pushFormFieldValue(parameterNames, boltConfig?.boltDiaValue);
  pushFormFieldValue(parameterNames, boltConfig?.boltQuantityValue);
  pushFormFieldValue(parameterNames, boltConfig?.boltLengthValue);
  pushFormFieldValue(parameterNames, boltConfig?.boltMaterialValue);
  pushFormFieldValue(parameterNames, boltConfig?.boltStandardValue);

  collectAdditionalCategoryTargets(additionalCategoriesConfig).forEach(
    (target) => {
      parameterNames.push(...target.parameterNames);
    },
  );
  parameterNames.push(...collectProcessConditionParameters(processConfig));
  parameterNames.push(...collectProjectPropertyParameters(processConfig));

  return sanitizeRevitParameterNames(parameterNames);
}
